/**
 * Per-league behaviour switches for the Props tool.
 *
 * League is a plain string throughout the tool (driven by the /props/:league route),
 * so nothing forces call sites to handle a new sport. Keep every "does this league
 * have X?" decision here instead of scattering `league === 'MLB'` checks around.
 */

export interface LeagueOption {
  value: string;
  label: string;
  disabled: boolean;
}

/**
 * Dev/demo only: serve locally generated NFL data instead of calling the API.
 * Set REACT_APP_PROPS_MOCK_NFL=true in .env.local (never in the deploy pipeline).
 */
export const isNflPropsMockEnabled = (): boolean =>
  process.env.REACT_APP_PROPS_MOCK_NFL === 'true';

/**
 * Local dev: call a locally running OddsShopper.MainApi directly (e.g. http://localhost:5233)
 * instead of the stokastic-site proxy. The internal API key is attached by the CRA dev proxy (src/setupProxy.js), never by the browser.
 */
export const getPropsLocalApiBase = (): string | undefined =>
  process.env.REACT_APP_PROPS_LOCAL_API?.replace(/\/$/, '') || undefined;

/** Either dev mode lets a local build skip the Stripe check (no product needed to work offline). */
export const isNflPropsDevBypass = (): boolean => isNflPropsMockEnabled() || !!getPropsLocalApiBase();

/** Single source of truth for the league pickers (dropdown + paywall overlays). */
export const getPropsLeagueOptions = (): LeagueOption[] => [
  { value: 'NBA', label: 'NBA', disabled: false },
  { value: 'NHL', label: 'NHL', disabled: false },
  { value: 'MLB', label: 'MLB', disabled: false },
  { value: 'NFL', label: 'NFL', disabled: false },
];

/**
 * League the tool lands on when the URL doesn't name one (/props, unsupported league, native-app
 * fallbacks). Seasonal: NFL while it's the live board so first-time visitors see a full slate.
 */
export const DEFAULT_PROPS_LEAGUE = 'NFL';

/** Leagues the Props tool can render. */
export const isPropsLeague = (league?: string): boolean =>
  !!league && ['NBA', 'NHL', 'MLB', 'NFL'].includes(league.toUpperCase());

/** League to link to from nav/landing for the current context; falls back to DEFAULT_PROPS_LEAGUE. */
export const getPropsRouteLeague = (league?: string): string => {
  const upper = league?.toUpperCase();
  return upper && isPropsLeague(upper) ? upper : DEFAULT_PROPS_LEAGUE;
};

const up = (league: string) => league.toUpperCase();

/** Projected minutes exist for NBA/NHL only. MLB and NFL have no minutes concept. */
export const leagueHasMinutes = (league: string): boolean => ['NBA', 'NHL'].includes(up(league));

/**
 * NBA/NHL player cards use a fixed stat config tied to the edit-rates model.
 * MLB/NFL build their stat columns from whatever markets the feed returns.
 */
export const leagueUsesDynamicStats = (league: string): boolean => ['MLB', 'NFL'].includes(up(league));

/**
 * Edit-rates / custom EV recalculation. NBA/NHL: minutes × per-minute rates.
 * NFL: volume (pass att / rush att / receptions) × per-unit rates; backend prices via fallback distributions.
 */
export const leagueHasEditRates = (league: string): boolean => ['NBA', 'NHL', 'NFL'].includes(up(league));

/** Position filter: NFL slates are large and roles are distinct (QB/RB/WR/TE/K). */
export const leagueHasPositionFilter = (league: string): boolean => up(league) === 'NFL';

/** NFL slates span Thu–Mon; group the game strip by kickoff day. */
export const leagueGroupsGamesByKickoff = (league: string): boolean => up(league) === 'NFL';

/** Injury report is meaningful for NBA and NFL. */
export const leagueHasInjuryReport = (league: string): boolean => ['NBA', 'NFL'].includes(up(league));

/**
 * NBA/NHL/MLB drop "questionable" players because that status usually means the
 * projection is unreliable. In the NFL "Questionable" is a routine weekly
 * designation and most of those players suit up, so they stay in the feed.
 */
export const leagueHidesQuestionable = (league: string): boolean => up(league) !== 'NFL';
